import { environment } from 'environment';
import { Role } from 'prismas';
import { io, Socket } from 'socket.io-client';
import {
  AppSocketClientToServerEvents,
  AppSocketServerToClientEvents,
  ObjEntries,
  ObjKeys,
  ReferralSocketClientToServerEvents,
  ReferralSocketServerToClientEvents,
} from 'types';
import { create } from 'zustand';
import { devtools } from './helpers';
import { useUser } from './user';

export type Sockets = {
  app?: Socket<AppSocketServerToClientEvents, AppSocketClientToServerEvents>;
  referral?: Socket<ReferralSocketServerToClientEvents, ReferralSocketClientToServerEvents>;
};

const socketDefinitions: { [key in keyof Sockets]: { url: string; roles: Role[] } } = {
  app: { url: `${environment.urls.api}/`, roles: [] },
  referral: {
    url: `${environment.urls.api}/referral`,
    roles: [Role.REFERRAL_PARTNER, Role.REFERRAL_MANAGER, Role.ADMIN],
  },
};

interface AppSocketState {
  sockets: Sockets;

  initSockets: () => void;
  killSockets: () => void;
}

export const useSockets = create<AppSocketState>()(
  devtools(
    (set, get) => ({
      sockets: {},

      initSockets: () => {
        if (typeof window === 'undefined') return;
        if (restoreSockets(set)) return;

        const sockets: Sockets = {};

        for (const [key, value] of ObjEntries(socketDefinitions)) {
          if (!useUser.getState()?.user?.roles.some(role => value.roles.includes(role))) continue;
          const socket = io(value.url, { withCredentials: true });
          if (environment.name === 'development')
            socket.onAny((event, ...args) => console.log(`${key} socket event:`, event, args));

          // socket.on('connect', () => console.log('connected'));
          // socket.on('disconnect', () => console.log('disconnected'));

          sockets[key] = socket;
        }

        // app socket
        // sockets.app.on(...)

        // referral socket
        // sockets.referral.on(...)

        set(s => ({ ...s, sockets }));
        if (environment.name === 'development') window.sockets = sockets;
      },
      killSockets: () => {
        if (typeof window === 'undefined') return;
        ObjKeys(get().sockets).forEach(key => get().sockets[key].disconnect());
        set(s => ({ ...s, sockets: {} }));
      },
    }),
    { name: 'sockets' },
  ),
);

function restoreSockets(set: (s: (s: AppSocketState) => AppSocketState) => void) {
  if (typeof window === 'undefined' || !window.sockets) return false;
  if (!ObjKeys(window.sockets).every(key => window.sockets[key].connected)) return false;
  set(s => ({ ...s, sockets: window.sockets }));
  return true;
}
